import type { SendState } from './delivery.ts';
import type { PaymentStatus, RegistrationSquad, RegistrationStatus, VerificationStatus } from './payment.ts';
import type { Role } from './permissions.ts';

export interface StatusLabel {
  th: string;
  /** Longer explanation shown in tooltips/legends. */
  hint: string;
  tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brand';
  icon: string; // lucide icon name; UI never relies on colour alone
}

export const SEND_STATE_LABELS: Record<SendState, StatusLabel> = {
  scheduled: { th: 'ตั้งเวลาไว้', hint: 'รอถึงเวลาตามกำหนด', tone: 'info', icon: 'calendar-clock' },
  queued: { th: 'เข้าคิวแล้ว', hint: 'รอ worker ส่งตามลำดับและอัตราที่อนุญาต', tone: 'info', icon: 'list-ordered' },
  dispatching: { th: 'กำลังส่ง', hint: 'ระบบกำลังส่งคำขอไปยังแพลตฟอร์ม', tone: 'info', icon: 'loader' },
  accepted: { th: 'แพลตฟอร์มรับคำขอแล้ว', hint: 'แพลตฟอร์มตอบรับคำขอ ยังไม่ใช่หลักฐานว่าผู้รับได้รับหรืออ่าน', tone: 'brand', icon: 'check' },
  delivered: { th: 'ส่งถึงแล้ว', hint: 'มีหลักฐานการส่งถึงจากแพลตฟอร์ม', tone: 'success', icon: 'check-check' },
  read: { th: 'อ่านแล้ว', hint: 'มีหลักฐานการอ่านจากแพลตฟอร์มตามขอบเขตที่รองรับ', tone: 'success', icon: 'eye' },
  retry_wait: { th: 'รอส่งซ้ำ', hint: 'ล้มเหลวชั่วคราว ระบบจะลองใหม่อัตโนมัติ', tone: 'warning', icon: 'rotate-cw' },
  failed: { th: 'ส่งไม่สำเร็จ', hint: 'ยืนยันความล้มเหลวและหมดรอบลองใหม่', tone: 'danger', icon: 'circle-x' },
  blocked: { th: 'ส่งไม่ได้', hint: 'ไม่มีสิทธิ์ส่ง ผู้รับยกเลิกรับ หรือบัญชีมีปัญหา', tone: 'danger', icon: 'ban' },
  skipped: { th: 'ข้าม', hint: 'ไม่เข้าเงื่อนไขล่าสุด ตัวแปรขาด หรือซ้ำ', tone: 'neutral', icon: 'skip-forward' },
  cancelled: { th: 'ยกเลิก', hint: 'ยกเลิกก่อนส่ง', tone: 'neutral', icon: 'circle-slash' },
  unknown: { th: 'ไม่ทราบผล', hint: 'การเชื่อมต่อหลุดหลังส่งคำขอ อาจส่งถึงแล้ว ห้ามส่งซ้ำโดยไม่ตรวจสอบ', tone: 'warning', icon: 'circle-help' },
};

/** "ตรวจสลิปอัตโนมัติ" (v1.33.0): what the slip verification service decided about one slip. */
export type SlipCheckStatus = 'verified' | 'duplicate' | 'wrong_account' | 'not_found' | 'unreadable' | 'error';
export const SLIP_CHECK_LABELS: Record<SlipCheckStatus, StatusLabel> = {
  verified: { th: 'สลิปถูกต้อง', hint: 'พบรายการโอนจริง และเข้าบัญชีของร้าน', tone: 'success', icon: 'circle-check' },
  duplicate: { th: 'สลิปซ้ำ', hint: 'เลขอ้างอิงนี้เคยส่งเข้ามาแล้ว', tone: 'warning', icon: 'copy-check' },
  wrong_account: { th: 'ไม่ใช่บัญชีร้าน', hint: 'โอนจริง แต่ปลายทางไม่ใช่บัญชีรับเงินของร้าน', tone: 'warning', icon: 'triangle-alert' },
  not_found: { th: 'ไม่พบรายการ', hint: 'ธนาคารไม่พบรายการโอนนี้ (อาจเป็นสลิปปลอมหรือรูปตัดต่อ)', tone: 'danger', icon: 'circle-x' },
  unreadable: { th: 'อ่านสลิปไม่ออก', hint: 'รูปไม่ชัด หรือไม่ใช่สลิปโอนเงิน', tone: 'neutral', icon: 'circle-help' },
  error: { th: 'ตรวจไม่สำเร็จ', hint: 'ติดต่อบริการตรวจสลิปไม่ได้ ให้แอดมินตรวจเอง', tone: 'neutral', icon: 'triangle-alert' },
};

/** "ยืนยันทาง LINE" of a registration (v1.31.0), derived by the server per registration. */
export type LineConfirmState = 'complete' | 'found' | 'linked_waiting' | 'pending';
export const LINE_CONFIRM_LABELS: Record<LineConfirmState, StatusLabel> = {
  complete: { th: 'สมัครสมบูรณ์', hint: 'ลูกค้าส่งข้อมูลใบสมัครนี้ทาง LINE แล้ว และยืนยันแล้วว่าบัญชี LINE เป็นคนเดียวกัน', tone: 'success', icon: 'circle-check' },
  found: { th: 'พบใน LINE · รอยืนยันบัญชี', hint: 'มีบัญชี LINE ที่เบอร์หรือชื่อทีมตรงกับใบสมัคร — กดยืนยันว่าเป็นคนเดียวกัน', tone: 'warning', icon: 'user-round' },
  linked_waiting: { th: 'รอข้อมูลทาง LINE', hint: 'ผูกบัญชี LINE แล้ว แต่ยังไม่พบข้อมูลของใบสมัครนี้ในแชท LINE', tone: 'info', icon: 'hourglass' },
  pending: { th: 'ยังไม่ยืนยันทาง LINE', hint: 'ลูกค้ายังไม่ได้ส่งข้อมูลสมัครทาง LINE', tone: 'neutral', icon: 'circle-slash' },
};

export const REGISTRATION_STATUS_LABELS: Record<RegistrationStatus, StatusLabel> = {
  interested: { th: 'สนใจ', hint: 'สอบถาม/สนใจ ยังไม่ได้สมัคร', tone: 'neutral', icon: 'sparkles' },
  pending_confirmation: { th: 'รอคอนเฟิร์ม', hint: 'สมัครแล้ว รอยืนยัน', tone: 'warning', icon: 'hourglass' },
  confirmed: { th: 'คอนเฟิร์มแล้ว', hint: 'ยืนยันการสมัครแล้ว (ไม่ได้หมายถึงชำระเงินครบ)', tone: 'success', icon: 'badge-check' },
  waitlisted: { th: 'รอคิว', hint: 'รุ่นเต็ม อยู่ในรายชื่อสำรอง', tone: 'info', icon: 'list-plus' },
  cancelled: { th: 'ยกเลิก', hint: 'ยกเลิกการสมัคร', tone: 'neutral', icon: 'circle-slash' },
  withdrawn: { th: 'ถอนทีม', hint: 'ทีมถอนออกจากรายการ · ตั้งกลับเป็นสถานะอื่นได้ทุกเมื่อ', tone: 'danger', icon: 'log-out' },
};

export const REGISTRATION_SQUAD_LABELS: Record<RegistrationSquad, StatusLabel> = {
  main: { th: 'ทีมหลัก', hint: 'อยู่ในจำนวนทีมหลักของรายการ', tone: 'success', icon: 'shield-check' },
  reserve: { th: 'ทีมสำรอง', hint: 'เป็นทีมสำรองของรุ่นนี้ ตามลำดับการสมัคร — เรียกเข้าแทนเมื่อมีทีมถอนตัว', tone: 'info', icon: 'list-plus' },
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, StatusLabel> = {
  unpaid: { th: 'ยังไม่ชำระ', hint: 'ยังไม่มีเงินที่ยืนยันแล้ว', tone: 'danger', icon: 'wallet' },
  partially_paid: { th: 'ค้างยอด', hint: 'ชำระบางส่วน (ยืนยันแล้ว)', tone: 'warning', icon: 'wallet-minimal' },
  paid: { th: 'ยอดครบ', hint: 'เงินที่ยืนยันแล้วเท่ากับยอดที่ต้องชำระ', tone: 'success', icon: 'circle-check' },
  overpaid: { th: 'ชำระเกิน', hint: 'เงินที่ยืนยันแล้วมากกว่ายอดที่ต้องชำระ', tone: 'info', icon: 'circle-plus' },
  refunded: { th: 'คืนเงินแล้ว', hint: 'มีการคืนเงินและไม่มียอดคงค้างที่ยืนยัน', tone: 'neutral', icon: 'undo-2' },
};

export const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, StatusLabel> = {
  pending_verification: { th: 'รอตรวจสอบ', hint: 'ได้รับหลักฐาน/สลิปแล้ว รอฝ่ายการเงินยืนยัน', tone: 'warning', icon: 'file-clock' },
  verified: { th: 'ยืนยันแล้ว', hint: 'ฝ่ายการเงินยืนยันแล้ว', tone: 'success', icon: 'shield-check' },
  rejected: { th: 'ปฏิเสธ', hint: 'หลักฐานไม่ถูกต้อง ไม่นับยอด', tone: 'danger', icon: 'shield-x' },
};

export const ROLE_LABELS: Record<Role, { th: string; hint: string }> = {
  owner: { th: 'เจ้าของ', hint: 'จัดการระบบ บัญชีเชื่อมต่อ ผู้ใช้ และสิทธิ์ทั้งหมด' },
  admin: { th: 'แอดมิน', hint: 'จัดการ CRM การส่งข้อความ หมวดหมู่ และมอบหมายแชท' },
  agent: { th: 'เจ้าหน้าที่แชท', hint: 'อ่าน/ตอบแชทตามสิทธิ์ แก้ข้อมูลที่อนุญาต' },
  finance: { th: 'การเงิน', hint: 'ยืนยัน/แก้รายการชำระเงิน' },
  viewer: { th: 'ผู้ดู', hint: 'ดูรายงานและแชทที่อนุญาต แก้ไข/ส่งไม่ได้' },
};

export const CAMPAIGN_STATUS_LABELS: Record<string, StatusLabel> = {
  draft: { th: 'ฉบับร่าง', hint: 'ยังไม่ยืนยันการส่ง', tone: 'neutral', icon: 'file-pen' },
  scheduled: { th: 'ตั้งเวลาแล้ว', hint: 'รอรอบส่งตามกำหนด', tone: 'info', icon: 'calendar-clock' },
  running: { th: 'กำลังส่ง', hint: 'มีรอบส่งกำลังทำงาน', tone: 'brand', icon: 'send' },
  paused: { th: 'หยุดชั่วคราว', hint: 'ไม่ส่งรายการใหม่จนกว่าจะกลับมาทำงาน', tone: 'warning', icon: 'pause' },
  completed: { th: 'เสร็จสิ้น', hint: 'ไม่มีรอบส่งเหลือ', tone: 'success', icon: 'flag' },
  cancelled: { th: 'ยกเลิก', hint: 'ยกเลิกแล้ว รายการที่แพลตฟอร์มรับไปแล้วเรียกคืนไม่ได้', tone: 'neutral', icon: 'circle-slash' },
  archived: { th: 'เก็บถาวร', hint: '', tone: 'neutral', icon: 'archive' },
};

export const SKIP_REASONS: Record<string, string> = {
  NO_LONGER_MATCHES: 'ไม่ตรงเงื่อนไขกลุ่มแล้ว ณ เวลาส่ง',
  MISSING_VARIABLE: 'ข้อมูลที่จำเป็นสำหรับตัวแปรไม่ครบ',
  DUPLICATE_IN_CAMPAIGN: 'ซ้ำภายในการส่งข้อความ',
  FREQUENCY_CAP: 'เกินเพดานความถี่การส่ง',
  SUPPRESSED: 'ยกเลิกรับข้อความ/ถูกระงับการส่ง',
  NO_DESTINATION: 'ไม่มีบัญชีช่องทางที่ส่งได้',
  NOT_ELIGIBLE: 'แพลตฟอร์มไม่อนุญาตให้ส่งถึงผู้รับนี้ในตอนนี้',
  OUTSIDE_WINDOW: 'อยู่นอกช่วงเวลาที่แพลตฟอร์มอนุญาต',
  CHANNEL_INACTIVE: 'บัญชีช่องทางไม่พร้อมใช้งาน',
  OVER_CAP: 'เกินจำนวนผู้รับสูงสุดของรอบ',
  CAMPAIGN_CANCELLED: 'การส่งข้อความถูกยกเลิก',
  CONTACT_REMOVED: 'ผู้ติดต่อถูกลบหรือรวมกับรายชื่ออื่น',
  REGISTRATION_CANCELLED: 'ใบสมัครถูกยกเลิก',
  PER_CONTACT_CAP: 'เกินจำนวนข้อความต่อคนที่กำหนด',
  MAX_ATTEMPTS: 'ลองส่งครบจำนวนครั้งสูงสุดแล้ว',
  RETRY_EXPIRED: 'หมดเวลาลองส่งใหม่',
};

/**
 * Inbox views (§6.1), in the order shown. Labels say plainly which chats appear.
 * `hidden` views stay valid in links and the API but are not listed (evidence-level detail the owner
 * found confusing, 17 Sep 2026 — delivery/read evidence remains visible on each message).
 */
export const INBOX_VIEWS = [
  { key: 'needs_reply', th: 'รอตอบ', icon: 'message-circle-reply' },
  // Assignment is back (owner, v1.42.0): an admin is put on a chat and it shows in their own list. Called
  // "มอบหมายงาน" since v1.79.3 — the owner reads it as the act of handing the chat to someone, not of taking it.
  // A flag, the same icon the chat menu hands the job out with (owner, 30 ก.ย. 2569: "จริงๆ ควรเป็น Icon ธง").
  { key: 'mine', th: 'มอบหมายงาน', icon: 'flag' },
  { key: 'unassigned', th: 'ยังไม่มีคนดูแล', icon: 'user-round-x', hidden: true },
  // A chat the admin marks "รอยืนยัน" by hand, like starring (owner, v1.42.0).
  { key: 'awaiting_confirm', th: 'รอยืนยันใบสมัคร', icon: 'circle-help' },
  { key: 'customer_read_no_reply', th: 'ลูกค้าอ่านแล้วแต่ไม่ตอบ', icon: 'eye' },
  { key: 'scheduled', th: 'ตั้งเวลาส่งไว้', icon: 'calendar-clock' },
  { key: 'send_problem', th: 'ส่งไม่สำเร็จ', icon: 'triangle-alert' },
  { key: 'closed', th: 'ปิดเรื่องแล้ว', icon: 'archive' },
  { key: 'all', th: 'ทั้งหมด', icon: 'inbox' },
  { key: 'delivered_not_read', th: 'ส่งถึงแล้ว ลูกค้ายังไม่อ่าน', icon: 'check-check', hidden: true },
  { key: 'sent_no_read_support', th: 'ส่งแล้ว (ช่องทางนี้ไม่บอกว่าอ่าน)', icon: 'check', hidden: true },
] as const;
export type InboxViewKey = (typeof INBOX_VIEWS)[number]['key'];

/** Channel capability names shown on the Connections screen (channels module). */
export const CAPABILITY_LABELS_TH: Record<string, string> = {
  inbound: 'รับข้อความเข้า',
  outboundText: 'ส่งข้อความ',
  outboundImage: 'ส่งรูปภาพ',
  historySync: 'ดึงประวัติแชทย้อนหลัง',
  deliveryReceipts: 'หลักฐานส่งถึง',
  readReceipts: 'หลักฐานการอ่าน',
  bulkSend: 'ส่งหลายคน',
  idempotentSend: 'กันส่งซ้ำจากฝั่งแพลตฟอร์ม',
  statusLookup: 'ตรวจสถานะข้อความย้อนหลัง',
  quotaSupported: 'ตรวจโควตาข้อความ',
};

export type ConnectionPurpose = 'customer' | 'admin_notify' | 'both' | 'page_post';

export const CONNECTION_PURPOSE_LABELS_TH: Record<ConnectionPurpose, string> = {
  customer: 'แชทลูกค้า',
  admin_notify: 'แจ้งเตือนผู้ดูแล',
  both: 'แชทลูกค้าและแจ้งเตือนผู้ดูแล',
  // A card that only carries the token of the second Meta app, the one allowed to write the Page's posts.
  page_post: 'สำหรับโพสต์และแก้ไขโพสต์บนเพจ',
};

/** What the operator may pick when adding a connection. The two admin-alert values stay readable for rows
 *  that already use them, but the shop does not use them and asked for them off the picker (26 ก.ย. 2569). */
export const CONNECTION_PURPOSE_CHOICES: ConnectionPurpose[] = ['customer', 'page_post'];

export const WEBHOOK_EVENT_STATE_LABELS_TH: Record<'pending' | 'processed' | 'failed' | 'ignored', string> = {
  pending: 'รอประมวลผล',
  processed: 'ประมวลผลแล้ว',
  failed: 'ประมวลผลไม่สำเร็จ',
  ignored: 'ไม่เกี่ยวข้อง (ข้าม)',
};

// ---------------------------------------------------------------- CRM (contacts, teams, events, ledger)
export const PAYMENT_ENTRY_TYPE_LABELS: Record<'payment' | 'refund' | 'adjustment' | 'reversal', { th: string; hint: string }> = {
  payment: { th: 'รับชำระ', hint: 'เงินที่ได้รับจากทีม/ผู้สมัคร' },
  refund: { th: 'คืนเงิน', hint: 'คืนเงินให้ทีม/ผู้สมัคร (บันทึกเป็นยอดติดลบ)' },
  adjustment: { th: 'ปรับยอด', hint: 'ปรับยอดพร้อมเหตุผล เช่น ส่วนลดหรือแก้ไขข้อผิดพลาด' },
  reversal: { th: 'กลับรายการ', hint: 'ยกเลิกผลของรายการเดิมด้วยยอดตรงข้าม (ไม่ลบประวัติ)' },
};

export const PAYMENT_METHOD_LABELS: Record<'cash' | 'transfer' | 'promptpay' | 'card' | 'imported' | 'other', string> = {
  cash: 'เงินสด',
  transfer: 'โอนเงิน',
  promptpay: 'พร้อมเพย์',
  card: 'บัตร',
  imported: 'นำเข้าจากระบบอื่น',
  other: 'อื่น ๆ',
};

export const TEAM_CONTACT_ROLE_LABELS: Record<'coach' | 'manager' | 'staff' | 'parent' | 'other', string> = {
  coach: 'โค้ช',
  manager: 'ผู้จัดการทีม',
  staff: 'สตาฟ',
  parent: 'ผู้ปกครอง',
  other: 'อื่น ๆ',
};

export const IDENTITY_LINK_STATE_LABELS: Record<'unlinked' | 'suggested' | 'verified', StatusLabel> = {
  unlinked: { th: 'ยังไม่ผูก', hint: 'บัญชีแชทยังไม่ผูกกับผู้ติดต่อ', tone: 'neutral', icon: 'unlink' },
  suggested: { th: 'แนะนำให้ตรวจสอบ', hint: 'ระบบพบความเป็นไปได้ ต้องมีหลักฐานก่อนยืนยัน', tone: 'warning', icon: 'circle-help' },
  verified: { th: 'ยืนยันแล้ว', hint: 'ผูกด้วยหลักฐาน (รหัสยืนยัน บัญชีสมัคร หรือแอดมินตรวจหลักฐาน)', tone: 'success', icon: 'link' },
};

export const EVENT_STATUS_LABELS: Record<'draft' | 'open' | 'closed' | 'completed' | 'cancelled', StatusLabel> = {
  draft: { th: 'ฉบับร่าง', hint: 'ยังไม่เปิดรับสมัคร', tone: 'neutral', icon: 'file-pen' },
  open: { th: 'เปิดรับสมัคร', hint: 'รับใบสมัครอยู่', tone: 'brand', icon: 'door-open' },
  closed: { th: 'ปิดรับสมัคร', hint: 'ไม่รับใบสมัครใหม่', tone: 'warning', icon: 'door-closed' },
  completed: { th: 'จบรายการแล้ว', hint: 'แข่งขันเสร็จสิ้น', tone: 'success', icon: 'flag' },
  cancelled: { th: 'ยกเลิกรายการ', hint: 'รายการถูกยกเลิก', tone: 'neutral', icon: 'circle-slash' },
};

export const DUPLICATE_REASON_LABELS: Record<'shared_phone' | 'same_team' | 'same_fb_name' | 'similar_name', string> = {
  same_team: 'ชื่อทีมเดียวกัน (ทีมอื่นอาจใช้ชื่อซ้ำ ต้องตรวจสอบ)',
  shared_phone: 'ใช้เบอร์โทรเดียวกัน (อาจเป็นเบอร์ทีม/ครอบครัว ต้องตรวจสอบ)',
  same_fb_name: 'ชื่อ Facebook ตรงกันทุกตัวอักษร · เป็นคนละบัญชีในระบบ (เช่น ทักคนละเพจ)',
  similar_name: 'ชื่อคล้ายกันมาก',
};

/** Admin notifications (§14): triggers, delivery states, destination verification. */
export const NOTIFICATION_TRIGGER_LABELS: Record<string, { th: string; hint: string }> = {
  inbound_new: { th: 'ข้อความใหม่จากลูกค้า', hint: 'ลูกค้าส่งข้อความเข้ามา ข้อความติดกันถูกรวมเป็นการแจ้งเตือนเดียวตามช่วงรวมข้อความ' },
  reply_after_campaign: { th: 'ตอบกลับภายหลังการส่งข้อความ', hint: 'ลูกค้าส่งข้อความภายใน 7 วันหลังได้รับการส่งข้อความ ไม่ได้ยืนยันว่าเป็นการตอบการส่งข้อความโดยตรง' },
  slip_uploaded: { th: 'แนบสลิปรอตรวจสอบ', hint: 'รูปภาพในแชทที่มียอดค้าง หรือมีรายการชำระเงินรอตรวจสอบ (ไม่ส่งรูปสลิปไปห้องแจ้งเตือน)' },
  unassigned: { th: 'แชทใหม่ยังไม่มีผู้รับผิดชอบ', hint: 'ข้อความแรกของแชทที่ยังไม่ได้มอบหมาย' },
  sla_breach: { th: 'รอตอบเกินเวลาที่กำหนด', hint: 'ไม่มีแอดมินตอบ รับมอบหมาย รับช่วง หรือปิดเรื่องภายในเวลาที่กำหนด' },
  queue_failures: { th: 'งานส่งล้มเหลวจำนวนมาก', hint: 'งานล้มเหลวถาวรและข้อความส่งไม่สำเร็จใน 15 นาทีเกินเกณฑ์ (ไม่นับการแจ้งเตือนผู้ดูแล)' },
  token_expired: { th: 'Token หมดอายุ/ถูกยกเลิกสิทธิ์', hint: 'บัญชีช่องทางใช้งานไม่ได้จนกว่าจะเชื่อมต่อใหม่' },
  quota_low: { th: 'โควตาข้อความใกล้หมด', hint: 'โควตาข้อความของบัญชี (เช่น LINE OA) ถูกใช้ถึงเกณฑ์ แจ้งเตือนและการส่งข้อความใช้โควตาร่วมกัน' },
  connection_degraded: { th: 'การเชื่อมต่อทำงานไม่เต็มที่', hint: 'บัญชีช่องทางมีปัญหาบางส่วน' },
  ai_escalated: { th: 'AI ส่งต่อให้แอดมิน', hint: 'AI ไม่มั่นใจหรือพบเรื่องที่ต้องให้คนตอบ' },
  system_alert: { th: 'แจ้งเตือนสุขภาพระบบ', hint: 'ดิสก์/หน่วยความจำ/CPU ใกล้เต็ม, worker หยุด, URL สาธารณะเข้าไม่ได้ (webhook หาย), ช่องทางเงียบผิดปกติ, token ใกล้หมดอายุ ฯลฯ — ระดับวิกฤตส่งทันทีไม่รอช่วงงดแจ้งเตือน' },
};

export const NOTIFICATION_DELIVERY_STATE_LABELS: Record<string, StatusLabel> = {
  collecting: { th: 'กำลังรวมข้อความ', hint: 'รอครบช่วงรวมข้อความ/สรุป หรือรอพ้นช่วงงดแจ้งเตือน', tone: 'info', icon: 'layers' },
  queued: { th: 'เข้าคิวแล้ว', hint: 'รอส่งตามลำดับและอัตราที่อนุญาต (ใช้โควตาร่วมกับการส่งข้อความ)', tone: 'info', icon: 'list-ordered' },
  sending: { th: 'กำลังส่ง', hint: 'ระบบกำลังส่งคำขอไปยังแพลตฟอร์ม', tone: 'info', icon: 'loader' },
  sent: { th: 'แพลตฟอร์มรับคำขอแล้ว', hint: 'ส่งคำขอสำเร็จ ยังไม่ใช่หลักฐานว่าผู้รับอ่าน', tone: 'brand', icon: 'check' },
  retry_wait: { th: 'รอส่งซ้ำ', hint: 'ล้มเหลวชั่วคราว ระบบจะลองใหม่อัตโนมัติ', tone: 'warning', icon: 'rotate-cw' },
  dead: { th: 'ส่งไม่สำเร็จ (รอตรวจสอบ)', hint: 'ลองครบจำนวนครั้งหรือผิดพลาดถาวร กดลองใหม่ได้เอง ระบบไม่แจ้งเตือนซ้ำเกี่ยวกับรายการนี้', tone: 'danger', icon: 'circle-x' },
  suppressed: { th: 'ไม่ส่ง (ปิดเสียง)', hint: 'กฎถูกปิดเสียงชั่วคราว', tone: 'neutral', icon: 'bell-off' },
  cancelled: { th: 'ยกเลิก', hint: 'กฎหรือปลายทางถูกปิด/ลบก่อนส่ง', tone: 'neutral', icon: 'circle-slash' },
};

export const NOTIFICATION_VERIFICATION_LABELS: Record<'pending' | 'verified' | 'disabled', StatusLabel> = {
  pending: { th: 'รอยืนยัน', hint: 'ผู้รับต้องส่งรหัสยืนยันจากแชท LINE OA หรือกด Start บอท Telegram ภายใน 30 นาที', tone: 'warning', icon: 'hourglass' },
  verified: { th: 'ยืนยันแล้ว', hint: 'ส่งการแจ้งเตือนไปยังแชทนี้ได้', tone: 'success', icon: 'shield-check' },
  disabled: { th: 'ปิดใช้งาน', hint: 'ไม่ส่งการแจ้งเตือนไปยังปลายทางนี้', tone: 'neutral', icon: 'bell-off' },
};

/** Sending / campaign reasons beyond SKIP_REASONS (dispatcher, Retry Center, scheduler). */
export const SENDING_REASON_LABELS: Record<string, string> = {
  EXPIRED: 'หมดอายุก่อนส่งได้',
  REVISION_SUPERSEDED: 'เนื้อหาการส่งข้อความถูกแก้ไข ระบบสร้างข้อความตาม revision ล่าสุดก่อนส่ง',
  MEDIA_UNAVAILABLE: 'รูปภาพถูกลบ ยังไม่พร้อม หรือแพลตฟอร์มดึงไฟล์ไม่ได้ จึงพักการส่งไว้',
  AMBIGUOUS_CONTEXT: 'ผู้ติดต่อมีหลายใบสมัคร ต้องเลือกส่งแยกตามใบสมัครหรือสรุปรวมรายการ',
  AUDIENCE_INVALID: 'เงื่อนไขกลุ่มตามเงื่อนไขของการส่งข้อความใช้งานไม่ได้แล้ว (เช่น กลุ่ม/หมวดถูกลบ)',
  CHANNEL_CAPABILITY_UNSUPPORTED: 'ช่องทางนี้ไม่รองรับเนื้อหาประเภทนี้',
  QUIET_HOURS: 'อยู่ในช่วงงดส่ง เลื่อนไปเวลาที่อนุญาต',
  WORKER_LOST: 'ระบบหยุดทำงานระหว่างส่งคำขอ ไม่ทราบว่าแพลตฟอร์มรับแล้วหรือไม่',
  MISFIRE: 'เลยเวลารอบส่งเกินช่วงผ่อนผัน (เช่น เครื่องปิดอยู่) จึงพักไว้ให้ตรวจสอบ',
  MANUAL_PAUSE: 'ผู้ใช้หยุดชั่วคราว',
  MANUAL_CONFIRMED_SENT: 'ผู้ใช้ตรวจสอบแล้วยืนยันว่าส่งถึงแพลตฟอร์มแล้ว',
  MANUAL_CONFIRMED_NOT_SENT: 'ผู้ใช้ตรวจสอบแล้วยืนยันว่ายังไม่ได้ส่ง',
  RECONCILED_NOT_SENT: 'ตรวจสอบกับแพลตฟอร์มแล้วว่ายังไม่ได้ส่ง จะลองส่งใหม่',
  NOT_ATTEMPTED: 'แพลตฟอร์มยังไม่ได้ประมวลผลส่วนนี้ของข้อความ',
  FALLBACK_CREATED: 'สร้างการส่งสำรองผ่านช่องทางอื่นตามนโยบายการส่งข้อความ',
  OTHER_CHANNEL_ACCOUNT: 'แชทที่เลือกอยู่ในบัญชีช่องทางอื่น ไม่ใช่บัญชีที่การส่งข้อความใช้ส่ง',
  NO_CONTACT: 'แชทยังไม่เชื่อมกับผู้ติดต่อ จึงตรวจการยกเลิกรับ/เพดานความถี่ไม่ได้',
};

/** Fixed Thai notices used by campaign/delivery APIs and UI. */
export const SENDING_NOTICES_TH = {
  notRecallable: 'ส่งไปแล้วหรือกำลังส่ง เรียกคืนไม่ได้',
  costUnconfirmed: 'ไม่ยืนยันค่าใช้จ่าย',
  repliedAfterSend: 'ตอบกลับภายหลังการส่ง',
  repliedCorrelated: 'ตอบกลับโดยอ้างอิงข้อความนี้',
  readNotSupported: 'ไม่รองรับข้อมูลการอ่าน',
  deliveredNotSupported: 'ไม่รองรับข้อมูลการส่งถึง',
  dynamicAudience: 'กลุ่มตามเงื่อนไขแบบไดนามิกประเมินใหม่ทุกรอบ ผู้รับอาจเพิ่มขึ้น ระบบจะพักรอบหากเกินจำนวนสูงสุด',
  unknownRisk: 'รายการไม่ทราบผลอาจส่งถึงแล้ว การลองใหม่อาจทำให้ผู้รับได้ข้อความซ้ำ',
} as const;

/** Campaign template variables ({{name}}). */
export const TEMPLATE_VARIABLE_LABELS: Record<string, string> = {
  contact_name: 'ชื่อผู้ติดต่อ',
  coach_name: 'ชื่อโค้ช',
  team_name: 'ชื่อทีม (ตามใบสมัคร)',
  division: 'รุ่น (ตามใบสมัคร)',
  event_title: 'ชื่อรายการแข่ง',
  event_date: 'วันแข่ง (พ.ศ.)',
  outstanding: 'ยอดคงเหลือ (บาท ตามใบสมัคร)',
  amount_due: 'ยอดที่ต้องชำระ (บาท ตามใบสมัคร)',
  registration_url: 'ลิงก์สมัคร/ชำระเงินของรายการ',
  registrations_summary: 'สรุปใบสมัครที่เกี่ยวข้อง (แยกรายการ ไม่รวมยอด)',
};

/** AI modes, run outcomes, draft states and knowledge statuses (§15). */
export type AiModeKey = 'inherit' | 'off' | 'draft_only' | 'auto_reply';
export const AI_MODE_LABELS: Record<AiModeKey, StatusLabel> = {
  inherit: { th: 'ตามค่าตั้งต้น', hint: 'ใช้นโยบายของหมวด บัญชีช่องทาง หรือค่าเริ่มต้นของระบบ', tone: 'neutral', icon: 'git-branch' },
  off: { th: 'ปิด AI', hint: 'AI ไม่ร่างและไม่ตอบในแชทนี้', tone: 'neutral', icon: 'bot-off' },
  draft_only: { th: 'ร่างคำตอบเท่านั้น', hint: 'AI ร่างคำตอบให้แอดมินตรวจและกดส่งเอง (ค่าเริ่มต้น)', tone: 'info', icon: 'file-pen' },
  auto_reply: { th: 'ตอบอัตโนมัติ (FAQ)', hint: 'AI ตอบเฉพาะหมวดที่อนุญาตเมื่อมั่นใจเกินเกณฑ์ นอกนั้นเป็นร่างให้แอดมิน', tone: 'brand', icon: 'bot' },
};

export type AiRunOutcomeKey = 'drafted' | 'auto_sent' | 'escalated' | 'refused' | 'skipped_takeover' | 'skipped_disabled' | 'error' | 'summarized';
export const AI_RUN_OUTCOME_LABELS: Record<AiRunOutcomeKey, StatusLabel> = {
  drafted: { th: 'ร่างคำตอบแล้ว', hint: 'รอแอดมินตรวจและกดใช้', tone: 'info', icon: 'file-pen' },
  auto_sent: { th: 'AI ตอบอัตโนมัติ', hint: 'ส่งคำตอบ FAQ ตามนโยบายตอบอัตโนมัติ', tone: 'brand', icon: 'bot' },
  escalated: { th: 'ส่งต่อแอดมิน', hint: 'AI ไม่มั่นใจหรือเป็นเรื่องที่ต้องให้คนตอบ', tone: 'warning', icon: 'user-round-cog' },
  refused: { th: 'AI ปฏิเสธ', hint: 'AI หรือระบบความปลอดภัยปฏิเสธคำขอ ต้องให้แอดมินตอบ', tone: 'warning', icon: 'shield-alert' },
  skipped_takeover: { th: 'ข้าม (แอดมินรับช่วง)', hint: 'มีแอดมินรับช่วงหรือตอบไปก่อน AI จึงไม่ตอบ', tone: 'neutral', icon: 'hand' },
  skipped_disabled: { th: 'ข้าม (ปิด/ยังไม่ตั้งค่า AI)', hint: 'นโยบายปิด AI หรือยังไม่ได้ตั้งค่า AI', tone: 'neutral', icon: 'bot-off' },
  error: { th: 'AI ขัดข้อง', hint: 'เรียกใช้ AI ไม่สำเร็จ แชทยังใช้งานได้ตามปกติ', tone: 'danger', icon: 'circle-x' },
  summarized: { th: 'สรุปแชทแล้ว', hint: 'สรุปตามคำขอของแอดมิน', tone: 'info', icon: 'scroll-text' },
};

export type AiDraftStateKey = 'pending' | 'used' | 'dismissed' | 'stale' | 'none';
export const AI_DRAFT_STATE_LABELS: Record<AiDraftStateKey, StatusLabel> = {
  pending: { th: 'รอตรวจ', hint: 'ร่างล่าสุด ยังไม่ได้ใช้', tone: 'info', icon: 'file-clock' },
  used: { th: 'ใช้แล้ว', hint: 'แอดมินกดใช้ร่างนี้ส่งถึงลูกค้า', tone: 'success', icon: 'send' },
  dismissed: { th: 'ปิดร่าง', hint: 'แอดมินไม่ใช้ร่างนี้', tone: 'neutral', icon: 'x' },
  stale: { th: 'ร่างเก่า', hint: 'มีข้อความหรือร่างใหม่กว่า ควรตรวจก่อนใช้', tone: 'neutral', icon: 'history' },
  none: { th: 'ไม่มีร่าง', hint: 'ไม่มีข้อความร่างที่ใช้ได้', tone: 'neutral', icon: 'minus' },
};

export type KnowledgeStatusKey = 'draft' | 'approved' | 'archived';
export const KNOWLEDGE_STATUS_LABELS: Record<KnowledgeStatusKey, StatusLabel> = {
  draft: { th: 'ฉบับร่าง', hint: 'ยังไม่อนุมัติ AI จะไม่ใช้รายการนี้', tone: 'neutral', icon: 'file-pen' },
  approved: { th: 'อนุมัติแล้ว', hint: 'AI ใช้ได้เมื่ออยู่ในช่วงวันที่มีผล', tone: 'success', icon: 'badge-check' },
  archived: { th: 'เก็บถาวร', hint: 'ไม่ใช้งานแล้ว', tone: 'neutral', icon: 'archive' },
};

export const AI_POLICY_SCOPE_LABELS: Record<'workspace' | 'channel_account' | 'folder', string> = {
  workspace: 'ค่าเริ่มต้นของระบบ',
  channel_account: 'บัญชีช่องทาง',
  folder: 'หมวดหมู่',
};

/** Customer read evidence support per channel (A14): unsupported is N/A, never "not read". */
export const READ_SUPPORT_LABELS: Record<'supported' | 'unsupported', StatusLabel> = {
  supported: { th: 'รองรับข้อมูลการอ่าน', hint: 'แสดงว่าอ่านแล้วเฉพาะเมื่อมีหลักฐานจากแพลตฟอร์ม', tone: 'info', icon: 'eye' },
  unsupported: { th: 'ไม่รองรับข้อมูลการอ่าน', hint: 'ช่องทางนี้ไม่ส่งหลักฐานการอ่าน จึงไม่สามารถบอกได้ว่าลูกค้าอ่านหรือยัง', tone: 'neutral', icon: 'eye-off' },
};

/** Conversation-level AI mode (spec §15); takeover always suppresses auto replies. */
export const CONVERSATION_AI_MODE_LABELS: Record<'inherit' | 'off' | 'draft_only' | 'auto_reply', string> = {
  inherit: 'ตามค่าช่องทาง/หมวด',
  off: 'ปิด AI',
  draft_only: 'AI ร่างคำตอบเท่านั้น',
  auto_reply: 'AI ตอบอัตโนมัติ',
};

/** CSV import batch states (folders/segments/imports/sync module). */
export const IMPORT_BATCH_STATUS_LABELS: Record<'dry_run' | 'committing' | 'committed' | 'rolled_back' | 'failed', StatusLabel> = {
  dry_run: { th: 'ตรวจสอบแล้ว (ยังไม่บันทึก)', hint: 'ตรวจทุกแถวแล้ว ยังไม่มีข้อมูลถูกเพิ่มหรือแก้ไข', tone: 'info', icon: 'file-search' },
  committing: { th: 'กำลังบันทึก', hint: 'ระบบกำลังบันทึกข้อมูลทีละชุด', tone: 'info', icon: 'loader' },
  committed: { th: 'บันทึกแล้ว', hint: 'บันทึกข้อมูลแล้ว ย้อนกลับได้ถ้ายังไม่มีการแก้ไขภายหลัง', tone: 'success', icon: 'circle-check' },
  rolled_back: { th: 'ย้อนกลับแล้ว', hint: 'ลบหรือยกเลิกข้อมูลที่ชุดนี้สร้าง รายการเงินถูกกลับรายการ ไม่ถูกลบ', tone: 'neutral', icon: 'undo-2' },
  failed: { th: 'บันทึกไม่สำเร็จ', hint: 'บันทึกได้บางส่วน กรุณาย้อนกลับชุดนี้แล้วลองใหม่', tone: 'danger', icon: 'circle-x' },
};

/** Read-only sync conflict states. */
export const SYNC_CONFLICT_STATE_LABELS: Record<'open' | 'accepted_source' | 'kept_local', StatusLabel> = {
  open: { th: 'รอตัดสินใจ', hint: 'ค่าที่แก้ในระบบนี้ต่างจากค่าในระบบต้นทาง', tone: 'warning', icon: 'git-compare' },
  accepted_source: { th: 'ใช้ค่าจากระบบต้นทาง', hint: 'ใช้ค่าจากระบบต้นทางและให้ซิงก์ดูแลฟิลด์นี้ต่อ', tone: 'success', icon: 'download' },
  kept_local: { th: 'คงค่าที่แก้ในระบบนี้', hint: 'ซิงก์จะไม่เขียนทับค่าที่แก้ด้วยมือ', tone: 'neutral', icon: 'lock' },
};
